import moment, { type unitOfTime } from 'moment';

/**
 * Converts a value to a fixed decimal number
 */
export const toFixDecimal = (val: number | string, limit: number = 2): number | string => {
  return isNaN(parseFloat(val as string))
    ? val
    : parseFloat(parseFloat(val as string)?.toFixed(limit));
};

/**
 * Converts to milliseconds if epoch is in seconds (10 digits)
 */
export const convertToMilliseconds = (epochSeconds: number): number => {
  // Ensure the input is in seconds (10 digits)
  if (epochSeconds.toString()?.length === 10) {
    // Append three zeros to convert it to milliseconds (13 digits)
    return epochSeconds * 1000;
  } else {
    // If it's already in milliseconds or not 10 digits, return as-is
    return epochSeconds;
  }
};

/**
 * Gets time difference between two timestamps
 */
export const getTimeDiffInMs = (
  start: number,
  end: number,
  unit: unitOfTime.Diff = 'milliseconds'
): number => {
  const startTime = convertToMilliseconds(start);
  const endTime = convertToMilliseconds(end);
  const diff = moment.utc(endTime).diff(moment.utc(startTime), unit);
  return unit === 'milliseconds' ? diff / 1000 : diff;
};

/**
 * Converts epoch to time string format
 */
export const convertEpochToTime = (epoch: number | string): string => {
  const dateString = epoch?.toString();
  return dateString?.length === 13
    ? dateString.substring(0, 10) + '.' + dateString.substring(10, dateString?.length)
    : dateString;
};

/**
 * Gets line graph series data formatted for charting
 */
export const getLineGraphSeriesData = (
  data: [string, string | number][],
  isInt: boolean = false
): [number, number][] => {
  return data.map((item) => {
    const itemVal = isInt ? parseInt(item[1] as string) : toFixDecimal(item[1], 7);
    return [parseInt(moment(item[0]).local().format('x')), itemVal as number];
  });
};

/**
 * Calculates dynamic width for columns based on header text
 */
export const calculateDynamicWidth = (headerText: string | number): number => {
  try {
    const context = document.createElement('canvas').getContext('2d');
    if (context) {
      context.font = '14px Arial';
      const headerWidth = context.measureText(headerText?.toString()).width;
      return headerWidth + 60; // Adding padding
    }
    return 180;
  } catch {
    return 180;
  }
};

/**
 * Format date helper (placeholder - needs proper implementation based on app's date formatting)
 */
export const formatdate = (value: Date | string, format: string): string => {
  return moment(value).format(format);
};
